import {
  ActiveFileType,
  TAB_KEYS,
  usePaymentDetail,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { usePdfPreview } from '@/components/InvoiceReportDetailDrawer/hooks/usePdfPreview';
import { ApprovingInvoiceReportFormInputResponse } from 'ap-openapi';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useConvertDocumentTypeKeyToValue,
  useDocumentTypeLabel,
} from '@/components/InvoiceReportDetailDrawer/ApplicationContent/hook';
import {
  createDocumentSelector,
  processBusinessDocuments,
  selectFirstBusinessDocument as selectFirstBusinessDocumentHelper,
  type DocumentSelectorDependencies,
  type ProcessedBusinessDocument,
} from '@/components/InvoiceReportDetailDrawer/helpers';

export const useBusinessDocuments = (
  item: ApprovingInvoiceReportFormInputResponse | undefined,
  baseURL: string
) => {
  const documentTypeLabel = useDocumentTypeLabel();
  const convertDocumentTypeKeyToValue = useConvertDocumentTypeKeyToValue();
  const { createUnifiedPreviewProps, selectAndShowFile, activeFile } =
    usePdfPreview({ baseURL });
  const { activeTab } = usePaymentDetail();

  // Create dependencies object for document selectors
  const selectorDependencies: DocumentSelectorDependencies = useMemo(
    () => ({
      createUnifiedPreviewProps,
      selectAndShowFile,
    }),
    [createUnifiedPreviewProps, selectAndShowFile]
  );

  // Process business documents with all necessary data
  const processedDocuments = useMemo(() => {
    return processBusinessDocuments(
      item?.input_additional_values,
      convertDocumentTypeKeyToValue,
      documentTypeLabel,
      baseURL
    );
  }, [
    item?.input_additional_values,
    convertDocumentTypeKeyToValue,
    documentTypeLabel,
    baseURL,
  ]);

  // Create selection function for business documents
  const selectDocumentFile = useCallback(
    (document: ProcessedBusinessDocument) => {
      return createDocumentSelector(document, selectorDependencies);
    },
    [selectorDependencies]
  );

  // Create selection function for first business document (tab synchronization)
  const selectFirstBusinessDocument = useCallback(() => {
    selectFirstBusinessDocumentHelper(processedDocuments, selectorDependencies);
  }, [processedDocuments, selectorDependencies]);

  // Watch for tab changes and select first business document when business documents tab is selected
  // Only activate if no business document is currently active
  useEffect(() => {
    if (
      activeTab === TAB_KEYS.TRANSACTION_DOCUMENTS &&
      processedDocuments?.length > 0 &&
      activeFile?.type !== ActiveFileType.BUSINESS_DOCUMENT
    ) {
      selectFirstBusinessDocument();
    }
  }, [
    activeTab,
    processedDocuments?.length,
    activeFile?.type,
    selectFirstBusinessDocument,
  ]);

  return {
    processedDocuments,
    selectDocumentFile,
    selectFirstBusinessDocument,
    activeFile,
  };
};
